import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import { downloadBlob } from "../../dp/utils/format";
import RatingButtons from "./RatingButtons";
import {
  APPLICATION_STATUS_LABEL,
  downloadResumeApi,
  fmtCpf,
  fmtDateTime,
  fmtPhone,
  getApplicationApi,
  updateApplicationApi,
  whatsappLink,
  type ApplicationDetail,
  type ApplicationStatus,
} from "../campanhasApi";

const STATUSES: ApplicationStatus[] = ["NOVO", "EM_ANALISE", "APROVADO", "REPROVADO"];

/** Candidato: dados, respostas, currículo, situação e observação. */
const ApplicationModal: React.FC<{ id: string; onClose: () => void; onSaved: () => void }> = ({ id, onClose, onSaved }) => {
  const [data, setData] = useState<ApplicationDetail | null>(null);
  const [status, setStatus] = useState<ApplicationStatus>("NOVO");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  // Avaliou aqui: ao fechar, a lista recarrega.
  const [rated, setRated] = useState(false);
  const close = () => (rated ? onSaved() : onClose());

  useEffect(() => {
    getApplicationApi(id)
      .then((d) => {
        setData(d);
        setStatus(d.status);
        setNotes(d.notes ?? "");
      })
      .catch((e) => {
        toast.error(e?.message);
        onClose();
      });
  }, [id, onClose]);

  const save = async () => {
    try {
      setBusy(true);
      await updateApplicationApi(id, { status, notes: notes.trim() || null });
      toast.success("Candidato atualizado.");
      onSaved();
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    if (!data) return;
    try {
      const blob = await downloadResumeApi(id);
      downloadBlob(blob, data.resumeFileName || `curriculo-${data.fullName}`);
    } catch (e: any) {
      toast.error(e?.message);
    }
  };

  return (
    <div className="ecoDialog__overlay">
      <div className="cmpModal" role="dialog" aria-modal="true" aria-labelledby="cmp-app-title">
        <div className="cmpModal__head">
          <div>
            <h2 id="cmp-app-title" className="cmpModal__title">
              {data?.fullName ?? "Candidato"}
            </h2>
            {data ? <p className="cmpHint">{data.campaign.name} · recebida em {fmtDateTime(data.createdAt)}</p> : null}
          </div>
          <button type="button" className="ecoBtn ecoBtn--icon" aria-label="Fechar" data-modal-close onClick={close} disabled={busy}>
            <EcoIcon name="x" size={18} />
          </button>
        </div>

        {!data ? (
          <p className="cmpLoading">Carregando...</p>
        ) : (
          <>
            <div className="cmpModal__rating">
              <span className="ecoLabel">Avaliação</span>
              <RatingButtons
                applicationId={id}
                value={data.rating}
                size="md"
                onChange={(rating) => {
                  setData({ ...data, rating, ratedBy: rating ? "você" : null, ratedAt: rating ? new Date().toISOString() : null });
                  setRated(true);
                }}
              />
              <span className="cmpHint">
                {data.rating ? `Avaliado por ${data.ratedBy ?? "—"} em ${fmtDateTime(data.ratedAt)}. Clique de novo para voltar a pendente.` : "Pendente: ainda ninguém avaliou."}
              </span>
            </div>

            <dl className="cmpDl">
              <dt>CPF</dt>
              <dd>{fmtCpf(data.cpf)}</dd>
              <dt>Celular</dt>
              <dd>
                {fmtPhone(data.phone)}{" "}
                <a className="ecoLinkBtn" href={whatsappLink(data.phone)} target="_blank" rel="noreferrer">
                  abrir no WhatsApp
                </a>
              </dd>
              <dt>E-mail</dt>
              <dd>{data.email || "—"}</dd>
              <dt>Cidade e bairro</dt>
              <dd>
                {data.city} · {data.neighborhood}
              </dd>
              <dt>Anúncio de origem</dt>
              <dd>{data.adPosition ? `Anúncio ${data.adPosition}` : "Link direto"}</dd>
              {data.answers.map((a) => (
                <React.Fragment key={a.question}>
                  <dt>{a.question}</dt>
                  <dd>{a.answer}</dd>
                </React.Fragment>
              ))}
              <dt>Currículo</dt>
              <dd>
                {data.hasResume ? (
                  <button type="button" className="ecoBtn ecoBtn--sm" onClick={resume}>
                    <EcoIcon name="download" size={16} />
                    Baixar currículo
                  </button>
                ) : (
                  "Não enviou"
                )}
              </dd>
            </dl>

            <div className="cmpGrid">
              <label className="ecoField">
                <span className="ecoLabel">Situação</span>
                <select className="ecoSelect" value={status} onChange={(e) => setStatus(e.target.value as ApplicationStatus)}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {APPLICATION_STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </label>
              <div className="ecoField">
                <span className="ecoLabel">Última revisão</span>
                <p className="cmpHint">{data.reviewedBy ? `${data.reviewedBy}, ${fmtDateTime(data.reviewedAt)}` : "Ninguém mexeu ainda"}</p>
              </div>
            </div>

            <label className="ecoField">
              <span className="ecoLabel">
                Observação <span className="ecoLabel__optional">(só a equipe vê)</span>
              </span>
              <textarea className="ecoInput cmpTextarea" rows={3} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>

            <div className="cmpFooter">
              <button type="button" className="ecoBtn" onClick={close} disabled={busy}>
                Fechar
              </button>
              <button type="button" className="ecoBtn ecoBtn--primary" onClick={save} disabled={busy}>
                {busy ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ApplicationModal;
